import { useState } from 'react'
import { createEvent, updateEvent } from '../../redux/event/manageEvents/actions'
import { CreateEventPayload, Event } from '../../redux/event/manageEvents/types'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import showToast from '../../Components/Common/SweetAlert/ToastAlert'
import { useLoading } from '../../context/LoadingContext'
import { selectUserTeamMemberInfo } from '../../redux/auth/selector'

const useEventFormHook = (eventId?: number) => {
  const dispatch = useAppDispatch()
  const { setLoading } = useLoading()
  const [error, setError] = useState<string | null>(null)

  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const handleSubmit = async (eventData: CreateEventPayload | Partial<Event>) => {
    setLoading(true)
    setError(null)

    try {
      // For event updates
      if (eventId) {
        const updatedEventData = {
          ...eventData,
          updatedBy: {
            email: teamMemberDetails.email,
            workdayId: teamMemberDetails.workdayId,
          },
        }
        // Update event
        await dispatch(updateEvent({ id: eventId, ...updatedEventData })).unwrap()

        showToast('success', 'Event updated successfully!')
      } else {
        const newEventData: CreateEventPayload = {
          ...eventData,
          createdBy: {
            email: teamMemberDetails?.email,
            workdayId: teamMemberDetails?.workdayId,
          },
        }

        // Create event
        await dispatch(createEvent(newEventData)).unwrap()

        showToast('success', 'Event created successfully!')
      }
    } catch (error) {
      showToast('error', 'Error processing event and participants')
      console.error('Error processing event:', error)
    } finally {
      setLoading(false)
    }
  }

  // Mark event as complete
  const handleComplete = async () => {
    if (!eventId) {
      console.error('Event ID is required to update isComplete')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const updatedEventData = {
        isCompleted: true,
        updatedBy: {
          email: teamMemberDetails?.email,
          workdayId: teamMemberDetails?.workdayId,
        },
      }

      await dispatch(updateEvent({ id: eventId, ...updatedEventData })).unwrap()
      showToast('success', 'Event completed successfully!')
    } catch (error) {
      showToast('error', 'Error updating event completion status')
      console.error('Error updating event completion status:', error)
      setError('An error occurred while updating the completion status.')
    } finally {
      setLoading(false)
    }
  }

  return { handleSubmit, handleComplete, error }
}

export default useEventFormHook
